import { useEffect } from 'react';

/**
 * NetworkMonitor component that intercepts and logs all fetch requests
 * This is a "silent" component that just adds global monitoring
 */
const NetworkMonitor: React.FC = () => {
  useEffect(() => {
    console.log('NetworkMonitor: Initializing fetch monitoring');
    
    // Store the original fetch function
    const originalFetch = window.fetch;
    
    // Override the global fetch with our interceptor
    window.fetch = async function(input: RequestInfo | URL, init?: RequestInit) {
      // Get URL string representation from the input parameter
      const url = typeof input === 'string' 
        ? input 
        : input instanceof URL 
          ? input.toString() 
          : input.url;
          
      const method = init?.method || 'GET';
      
      console.log(`NetworkMonitor: Fetch request - ${method} ${url}`);
      
      if (init?.body) {
        try {
          const bodyText = typeof init.body === 'string' 
            ? init.body 
            : JSON.stringify(init.body);
          console.log(`NetworkMonitor: Request body - ${bodyText.substring(0, 200)}${bodyText.length > 200 ? '...' : ''}`);
        } catch (e) {
          console.log('NetworkMonitor: Could not stringify request body');
        }
      }
      
      // Log headers excluding sensitive ones
      if (init?.headers) {
        const headers = init.headers as Record<string, string>;
        const safeHeaders = { ...headers };
        
        // Remove sensitive headers for logging
        if (safeHeaders.Authorization) {
          safeHeaders.Authorization = safeHeaders.Authorization.substring(0, 15) + '...';
        }
        
        console.log('NetworkMonitor: Request headers -', safeHeaders);
      }
      
      // Start timer for request
      const startTime = performance.now();
      
      try {
        // Call the original fetch
        const response = await originalFetch(input, init);
        
        // Calculate request time
        const time = performance.now() - startTime;
        
        // Clone the response so we can inspect its body without consuming it
        const clone = response.clone();
        
        // Log response status
        console.log(`NetworkMonitor: Response - ${response.status} ${response.statusText} (${time.toFixed(2)}ms)`);
        
        // Try to log the response body for JSON responses
        if (clone.headers.get('content-type')?.includes('application/json')) {
          try {
            const data = await clone.json();
            console.log('NetworkMonitor: Response body -', data);
          } catch (e) {
            console.log('NetworkMonitor: Could not parse JSON response');
          }
        }
        
        return response;
      } catch (error) {
        // Calculate request time even for errors
        const time = performance.now() - startTime;
        console.error(`NetworkMonitor: Request failed after ${time.toFixed(2)}ms -`, error);
        throw error;
      }
    };
    
    // Restore original fetch on cleanup
    return () => {
      window.fetch = originalFetch;
      console.log('NetworkMonitor: Fetch monitoring removed');
    };
  }, []);
  
  // This component doesn't render anything
  return null;
};

export default NetworkMonitor; 